import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { pollJob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Empty, Spinner } from "@/components/common";
import { toast } from "sonner";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid,
} from "recharts";
import { Sparkles, Loader2, Plus, Trash2, LayoutGrid, Table2, GanttChartSquare, ShieldAlert, PieChart as PieIcon, FileBarChart } from "lucide-react";

const COLS = [{ k: "todo", label: "To Do" }, { k: "in_progress", label: "In Progress" }, { k: "done", label: "Done" }];
const PRIORITY = { High: "#ef4444", Medium: "#c9a227", Low: "#29abe2" };
const CHART = ["#0f2557", "#29abe2", "#c9a227", "#10b981", "#8b5cf6"];

function Kanban({ tasks, onMove, onDelete }) {
  const [dragId, setDragId] = useState(null);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {COLS.map((col) => (
        <div key={col.k} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragId) { onMove(dragId, col.k); setDragId(null); } }}
          className="rounded-xl border bg-secondary/30 p-3" data-testid={`kanban-col-${col.k}`}>
          <div className="flex items-center justify-between mb-3 px-1">
            <span className="text-sm font-semibold text-foreground">{col.label}</span>
            <span className="text-xs bg-card border rounded-full px-2 py-0.5 text-muted-foreground">{tasks.filter((t) => t.status === col.k).length}</span>
          </div>
          <div className="space-y-2 min-h-[80px]">
            {tasks.filter((t) => t.status === col.k).map((t) => (
              <div key={t.id} draggable onDragStart={() => setDragId(t.id)} data-testid={`task-${t.id}`}
                className="group rounded-lg border bg-card p-3 cursor-grab active:cursor-grabbing card-hover">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm text-foreground">{t.title}</span>
                  <button onClick={() => onDelete(t.id)} className="opacity-0 group-hover:opacity-100 text-red-400"><Trash2 size={13} /></button>
                </div>
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  <span className="text-[10px] px-1.5 py-0.5 rounded text-white" style={{ background: PRIORITY[t.priority] || "#5c6b84" }}>{t.priority}</span>
                  {t.phase && <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">{t.phase}</span>}
                  {t.module && <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">{t.module}</span>}
                  {t.owner && <span className="text-[10px] text-muted-foreground">👤 {t.owner}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function GanttView({ tasks }) {
  const ordered = [...tasks].filter((t) => t.type !== "action");
  let offset = 0;
  const bars = ordered.map((t) => {
    const dur = Number(t.estimate_days) || 1;
    const start = offset; offset += dur;
    return { ...t, start, dur };
  });
  const total = offset || 1;
  return (
    <div className="rounded-xl border bg-card p-5 overflow-x-auto" data-testid="gantt">
      <div className="min-w-[600px] space-y-1.5">
        {bars.map((b) => (
          <div key={b.id} className="flex items-center gap-3 text-xs">
            <div className="w-48 truncate text-foreground shrink-0">{b.title}</div>
            <div className="flex-1 relative h-6">
              <div className="absolute h-6 rounded-md flex items-center px-2 text-white text-[10px] font-medium"
                style={{ left: `${(b.start / total) * 100}%`, width: `${(b.dur / total) * 100}%`, background: b.status === "done" ? "#10b981" : b.phase === "Deploy" ? "#c9a227" : "#29abe2", minWidth: "40px" }}>
                {b.dur}d
              </div>
            </div>
          </div>
        ))}
        {bars.length === 0 && <p className="text-sm text-muted-foreground">No scheduled tasks. Generate a plan to build the timeline.</p>}
      </div>
    </div>
  );
}

function Dashboard({ pid }) {
  const { data } = useQuery({ queryKey: ["dashboard", pid], queryFn: async () => (await api.get(`/projects/${pid}/dashboard`)).data });
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!data) return <Spinner />;
  const statusData = Object.entries(data.by_status || {}).map(([k, v]) => ({ name: k, value: v }));
  const ownerData = Object.entries(data.by_owner || {}).map(([k, v]) => ({ name: k, value: v }));
  const riskData = Object.entries(data.risk_heat || {}).map(([k, v]) => ({ name: k, value: v }));

  const genReport = async () => {
    setBusy(true);
    try { const res = await api.post(`/projects/${pid}/weekly-report`); const r = await pollJob(res.data.job_id); setReport(r.report); }
    catch (e) { toast.error(e.message || "Could not generate report"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Progress", `${data.progress}%`], ["Requirements", data.requirements], ["Documents", data.documents], ["RAID items", data.raid_total]].map(([l, v]) => (
          <div key={l} className="rounded-xl border bg-card p-5"><div className="text-2xl font-bold text-foreground">{v}</div><div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{l}</div></div>
        ))}
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="rounded-xl border bg-card p-5">
          <div className="text-sm font-semibold mb-3">Tasks by status</div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart><Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} label>{statusData.map((e, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border bg-card p-5">
          <div className="text-sm font-semibold mb-3">Tasks by owner</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={ownerData}><CartesianGrid strokeDasharray="3 3" opacity={0.2} /><XAxis dataKey="name" fontSize={10} /><YAxis fontSize={10} allowDecimals={false} /><Tooltip /><Bar dataKey="value" fill="#29abe2" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border bg-card p-5">
          <div className="text-sm font-semibold mb-3">Risk heat map</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={riskData}><CartesianGrid strokeDasharray="3 3" opacity={0.2} /><XAxis dataKey="name" fontSize={10} /><YAxis fontSize={10} allowDecimals={false} /><Tooltip /><Bar dataKey="value" radius={[4, 4, 0, 0]}>{riskData.map((e, i) => <Cell key={i} fill={e.name === "High" ? "#ef4444" : e.name === "Medium" ? "#c9a227" : "#29abe2"} />)}</Bar></BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="rounded-xl border bg-card p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold flex items-center gap-2"><FileBarChart size={15} /> AI Weekly Status Report</div>
          <Button size="sm" variant="outline" onClick={genReport} disabled={busy} className="gap-2" data-testid="gen-report">{busy ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />} Generate</Button>
        </div>
        {report ? <pre className="text-sm text-foreground whitespace-pre-wrap font-sans leading-relaxed">{report}</pre> : <p className="text-sm text-muted-foreground">Generate an AI-written weekly status report from current task progress.</p>}
      </div>
    </div>
  );
}

function RaidLog({ pid }) {
  const { data, refetch } = useQuery({ queryKey: ["raid", pid], queryFn: async () => (await api.get(`/projects/${pid}/raid`)).data });
  const [form, setForm] = useState({ kind: "Risk", description: "", severity: "Medium", owner: "", mitigation: "" });
  const add = async () => {
    if (!form.description) { toast.error("Add a description"); return; }
    await api.post(`/projects/${pid}/raid`, form); setForm({ ...form, description: "", owner: "", mitigation: "" }); refetch();
  };
  const del = async (id) => { await api.delete(`/raid/${id}`); refetch(); };
  return (
    <div className="space-y-4">
      <div className="rounded-xl border bg-card p-4 grid grid-cols-1 sm:grid-cols-[8rem_1fr_8rem_1fr_auto] gap-2 items-center">
        <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}><SelectTrigger data-testid="raid-kind"><SelectValue /></SelectTrigger><SelectContent>{["Risk", "Assumption", "Issue", "Dependency"].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent></Select>
        <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="raid-desc" />
        <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["Low", "Medium", "High"].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent></Select>
        <Input placeholder="Owner / mitigation" value={form.mitigation} onChange={(e) => setForm({ ...form, mitigation: e.target.value })} />
        <Button onClick={add} className="gap-1" data-testid="raid-add"><Plus size={14} /> Add</Button>
      </div>
      {!data?.length ? <Empty title="No RAID items" hint="Log risks, assumptions, issues and dependencies here." /> : (
        <div className="space-y-2">
          {data.map((r) => (
            <div key={r.id} className="rounded-lg border bg-card p-3 flex items-center gap-3" data-testid={`raid-${r.id}`}>
              <span className="text-[10px] font-semibold px-2 py-1 rounded w-24 text-center" style={{ background: `${PRIORITY[r.severity]}22`, color: PRIORITY[r.severity] }}>{r.kind}</span>
              <div className="flex-1"><div className="text-sm text-foreground">{r.description}</div>{r.mitigation && <div className="text-xs text-muted-foreground">{r.mitigation}</div>}</div>
              <span className="text-xs text-muted-foreground">{r.severity}</span>
              <button onClick={() => del(r.id)} className="text-red-400"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PmHub({ pid, meetings }) {
  const { data: tasks, refetch, isLoading } = useQuery({ queryKey: ["tasks", pid], queryFn: async () => (await api.get(`/projects/${pid}/tasks`)).data });
  const [busy, setBusy] = useState(false);
  const analyzed = meetings.find((m) => m.status === "analyzed") || meetings[0];

  const move = async (id, status) => {
    const fd = new FormData(); fd.append("status", status);
    await api.patch(`/tasks/${id}/status`, fd, { headers: { "Content-Type": "multipart/form-data" } }); refetch();
  };
  const del = async (id) => { await api.delete(`/tasks/${id}`); refetch(); };

  const genPlan = async () => {
    if (!analyzed) { toast.error("Create and analyze a meeting first"); return; }
    setBusy(true);
    const fd = new FormData(); fd.append("meeting_id", analyzed.id);
    try { const res = await api.post(`/projects/${pid}/generate-plan`, fd, { timeout: 60000 }); const r = await pollJob(res.data.job_id); toast.success(`Generated ${r.tasks} tasks, ${r.actions} action items`); refetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message || "Plan generation failed"); } finally { setBusy(false); }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-muted-foreground">{(tasks || []).length} tasks · auto-generated from requirements & action items</div>
        <Button onClick={genPlan} disabled={busy} className="gap-2" data-testid="generate-plan">{busy ? <Loader2 className="animate-spin" size={15} /> : <Sparkles size={15} />} Generate plan from requirements</Button>
      </div>

      {(tasks || []).length === 0 ? (
        <Empty title="No plan yet" hint="Generate a work breakdown (Epics → Tasks) and action items from your analyzed meeting." />
      ) : (
        <Tabs defaultValue="kanban">
          <TabsList>
            <TabsTrigger value="kanban" data-testid="pm-kanban"><LayoutGrid size={14} className="mr-1.5" /> Kanban</TabsTrigger>
            <TabsTrigger value="table" data-testid="pm-table"><Table2 size={14} className="mr-1.5" /> Table</TabsTrigger>
            <TabsTrigger value="gantt" data-testid="pm-gantt"><GanttChartSquare size={14} className="mr-1.5" /> Gantt</TabsTrigger>
            <TabsTrigger value="raid" data-testid="pm-raid"><ShieldAlert size={14} className="mr-1.5" /> RAID</TabsTrigger>
            <TabsTrigger value="dashboard" data-testid="pm-dashboard"><PieIcon size={14} className="mr-1.5" /> Dashboard</TabsTrigger>
          </TabsList>
          <TabsContent value="kanban" className="mt-4"><Kanban tasks={tasks} onMove={move} onDelete={del} /></TabsContent>
          <TabsContent value="table" className="mt-4">
            <div className="rounded-xl border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-secondary/60 text-left">{["Task", "Phase", "Module", "Owner", "Priority", "Est.", "Status"].map((h) => <th key={h} className="px-4 py-2.5 font-medium text-muted-foreground">{h}</th>)}</tr></thead>
                <tbody>
                  {tasks.map((t) => (
                    <tr key={t.id} className="border-t">
                      <td className="px-4 py-2.5 text-foreground">{t.title}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{t.phase}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{t.module || "—"}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{t.owner || "—"}</td>
                      <td className="px-4 py-2.5"><span className="text-[10px] px-1.5 py-0.5 rounded text-white" style={{ background: PRIORITY[t.priority] || "#5c6b84" }}>{t.priority}</span></td>
                      <td className="px-4 py-2.5 text-muted-foreground">{t.estimate_days}d</td>
                      <td className="px-4 py-2.5">
                        <Select value={t.status} onValueChange={(v) => move(t.id, v)}><SelectTrigger className="h-7 w-32"><SelectValue /></SelectTrigger><SelectContent>{COLS.map((c) => <SelectItem key={c.k} value={c.k}>{c.label}</SelectItem>)}</SelectContent></Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
          <TabsContent value="gantt" className="mt-4"><GanttView tasks={tasks} /></TabsContent>
          <TabsContent value="raid" className="mt-4"><RaidLog pid={pid} /></TabsContent>
          <TabsContent value="dashboard" className="mt-4"><Dashboard pid={pid} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
